import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  ArrowLeft, Scale, Users, Gavel, Clock, History, FileStack, Target, DollarSign, ListChecks,
  Plus, Trash2, Edit, FileText, Upload, Download, Eye, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Máscara de moeda: o campo guarda sempre um decimal limpo ("1500.50"); a exibição formata
// esse valor como "1.500,50" e a digitação funciona por dígitos (os 2 últimos são os centavos).
const formatCurrencyDisplay = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(num) ? '' : num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseCurrencyInput = (rawText) => {
  const digits = rawText.replace(/\D/g, '');
  return digits ? (parseInt(digits, 10) / 100).toFixed(2) : '';
};

const STATUS_COLOR = {
  'Ativo': 'bg-blue-100 text-blue-800',
  'Suspenso': 'bg-yellow-100 text-yellow-800',
  'Encerrado': 'bg-gray-100 text-gray-800',
  'Arquivado': 'bg-gray-100 text-gray-600',
};

const RISCO_COLOR = {
  'Baixo': 'bg-green-100 text-green-800',
  'Médio': 'bg-orange-100 text-orange-800',
  'Alto': 'bg-red-100 text-red-800',
};

const PAPEL_OPTIONS = ['Autor', 'Réu', 'Terceiro', 'Interessado'];
const TIPO_PESSOA_OPTIONS = ['Pessoa Física', 'Pessoa Jurídica'];
const MODALIDADE_OPTIONS = ['Presencial', 'Virtual', 'Híbrida'];
const AUDIENCIA_STATUS_OPTIONS = ['Agendada', 'Realizada', 'Cancelada'];
const LEMBRETE_REPETIR_OPTIONS = ['Não repetir', 'Diariamente', 'Semanalmente'];
const PRAZO_TIPO_OPTIONS = ['Alegações finais', 'Manifestação', 'Recurso', 'Contestação', 'Outro'];
const PRIORIDADE_OPTIONS = ['Baixa', 'Média', 'Alta', 'Crítica'];
const PRAZO_STATUS_OPTIONS = ['Pendente', 'Em andamento', 'Concluído', 'Perdido'];
const MOVIMENTACAO_TIPO_OPTIONS = ['Tribunal', 'Movimentação interna', 'Despacho', 'Documento', 'Audiência'];
const DOCUMENTO_CATEGORIA_OPTIONS = ['Petição inicial', 'Procuração', 'Contrato social', 'Ata de audiência', 'Degravação', 'Prova documental', 'Outro'];
const PROBABILIDADE_PERDA_OPTIONS = ['Remota', 'Possível', 'Provável', 'Alta'];
const TESTEMUNHA_STATUS_OPTIONS = ['Arrolada', 'Intimada', 'Ouvida', 'Dispensada'];

const PRAZO_STATUS_COLOR = {
  'Pendente': 'bg-orange-100 text-orange-800',
  'Em andamento': 'bg-blue-100 text-blue-800',
  'Concluído': 'bg-green-100 text-green-800',
  'Perdido': 'bg-red-100 text-red-800',
};

const AUDIENCIA_STATUS_COLOR = {
  'Agendada': 'bg-blue-100 text-blue-800',
  'Realizada': 'bg-green-100 text-green-800',
  'Cancelada': 'bg-gray-100 text-gray-800',
};

const PRIORIDADE_COLOR = {
  'Baixa': 'bg-gray-100 text-gray-800',
  'Média': 'bg-orange-100 text-orange-800',
  'Alta': 'bg-red-100 text-red-800',
  'Crítica': 'bg-red-200 text-red-900',
};

const MOVIMENTACAO_DOT_COLOR = {
  'Tribunal': 'bg-blue-500',
  'Movimentação interna': 'bg-gray-400',
  'Despacho': 'bg-orange-500',
  'Documento': 'bg-blue-500',
  'Audiência': 'bg-green-500',
};

const InfoRow = ({ label, value }) => (
  <div className="grid grid-cols-[160px_1fr] gap-2 py-1.5 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-gray-900">{value ?? '-'}</span>
  </div>
);

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('pt-BR');
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

const todayDate = () => new Date().toISOString().slice(0, 10);
const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const isoToDateTimeParts = (iso) => {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return { date, time };
};

const GestaoProcessoDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [processo, setProcesso] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumo');
  const fileInputRef = useRef(null);

  const [showParteModal, setShowParteModal] = useState(false);
  const [editingParteId, setEditingParteId] = useState(null);
  const [parteForm, setParteForm] = useState({ papel: 'Autor', nome: '', tipo_pessoa: 'Pessoa Física', documento: '', representante: '', notas: '' });

  const [showTestemunhaModal, setShowTestemunhaModal] = useState(false);
  const [testemunhaForm, setTestemunhaForm] = useState({ nome: '', contato: '', assunto: '', audiencia_id: '', notas: '' });

  const [showAudienciaModal, setShowAudienciaModal] = useState(false);
  const AUDIENCIA_FORM_DEFAULT = {
    tipo: '', data: todayDate(), hora: nowTime(), modalidade: 'Presencial', local_ou_plataforma: '', link: '', participantes: '', notas: '',
    lembrete_ativo: false, lembrete_email: '', lembrete_data: '', lembrete_hora: '', lembrete_repetir: 'Não repetir',
  };
  const [audienciaForm, setAudienciaForm] = useState(AUDIENCIA_FORM_DEFAULT);
  const [editingAudienciaId, setEditingAudienciaId] = useState(null);

  const [showPrazoModal, setShowPrazoModal] = useState(false);
  const [prazoForm, setPrazoForm] = useState({ titulo: '', tipo: '', data_limite: '', hora_limite: '', responsavel: '', prioridade: 'Baixa', providencia: '' });

  const [showMovimentacaoModal, setShowMovimentacaoModal] = useState(false);
  const [movimentacaoForm, setMovimentacaoForm] = useState({ tipo: 'Tribunal', titulo: '', descricao: '', data: '', geraPrazo: 'Não', notificar: 'Sim' });

  const [showDocumentoModal, setShowDocumentoModal] = useState(false);
  const [documentoForm, setDocumentoForm] = useState({ nome: '', categoria: '', tamanho: '', notas: '' });
  const [documentoFiles, setDocumentoFiles] = useState([]);

  const [showEstrategiaModal, setShowEstrategiaModal] = useState(false);
  const [estrategiaForm, setEstrategiaForm] = useState({ tese_principal: '', pontos_fortes: '', pontos_fracos: '', provas_disponiveis: '', proxima_providencia: '', observacao_confidencial: '' });

  const [showFinanceiroModal, setShowFinanceiroModal] = useState(false);
  const [financeiroForm, setFinanceiroForm] = useState({ valor_causa: '', exposicao_estimada: '', valor_provisionado: '', custas_processuais: '', honorarios_previstos: '', probabilidade_perda: '' });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadProcesso();
    loadHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadProcesso = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/processos-juridicos/${id}`, authHeaders());
      setProcesso(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar processo');
    } finally {
      setLoading(false);
    }
  };

  const loadHistorico = async () => {
    try {
      const response = await axios.get(`${API}/api/processos-juridicos/${id}/historico`, authHeaders());
      setHistorico(response.data);
    } catch (error) {
      setHistorico([]);
    }
  };

  const exportHistorico = () => {
    if (historico.length === 0) {
      toast.error('Não há histórico para exportar');
      return;
    }
    const header = 'Data;Ação;Descrição';
    const rows = historico.map((h) => [
      formatDateTime(h.performed_at),
      h.action,
      (h.description || '').replace(/;/g, ','),
    ].join(';'));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historico-${processo?.numero_processo || id}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // --- Partes ---

  const openAddParte = () => {
    setEditingParteId(null);
    setParteForm({ papel: 'Autor', nome: '', tipo_pessoa: 'Pessoa Física', documento: '', representante: '', notas: '' });
    setShowParteModal(true);
  };

  const openEditParte = (parte) => {
    setEditingParteId(parte.id);
    setParteForm({
      papel: parte.papel, nome: parte.nome, tipo_pessoa: parte.tipo_pessoa || 'Pessoa Física',
      documento: parte.documento || '', representante: parte.representante || '', notas: parte.notas || '',
    });
    setShowParteModal(true);
  };

  const submitParte = async () => {
    if (!parteForm.nome.trim()) { toast.error('Informe o nome da parte'); return; }
    try {
      setSaving(true);
      let response;
      if (editingParteId) {
        response = await axios.put(`${API}/api/processos-juridicos/${id}/partes/${editingParteId}`, parteForm, authHeaders());
        toast.success('Parte atualizada');
      } else {
        response = await axios.post(`${API}/api/processos-juridicos/${id}/partes`, parteForm, authHeaders());
        toast.success('Parte adicionada');
      }
      setProcesso(response.data);
      setShowParteModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar parte');
    } finally {
      setSaving(false);
    }
  };

  const removeParte = async (parteId) => {
    if (!window.confirm('Remover esta parte?')) return;
    try {
      const response = await axios.delete(`${API}/api/processos-juridicos/${id}/partes/${parteId}`, authHeaders());
      setProcesso(response.data);
      toast.success('Parte removida');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover parte');
    }
  };

  // --- Testemunhas ---

  const openAddTestemunha = () => {
    setTestemunhaForm({ nome: '', contato: '', assunto: '', audiencia_id: '', notas: '' });
    setShowTestemunhaModal(true);
  };

  const submitTestemunha = async () => {
    if (!testemunhaForm.nome.trim()) { toast.error('Informe o nome da testemunha'); return; }
    try {
      setSaving(true);
      const payload = { ...testemunhaForm, audiencia_id: testemunhaForm.audiencia_id || null };
      const response = await axios.post(`${API}/api/processos-juridicos/${id}/testemunhas`, payload, authHeaders());
      setProcesso(response.data);
      toast.success('Testemunha adicionada');
      setShowTestemunhaModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar testemunha');
    } finally {
      setSaving(false);
    }
  };

  const updateTestemunhaStatus = async (testemunhaId, status) => {
    try {
      const response = await axios.put(`${API}/api/processos-juridicos/${id}/testemunhas/${testemunhaId}`, { status }, authHeaders());
      setProcesso(response.data);
      toast.success('Testemunha atualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar testemunha');
    }
  };

  const removeTestemunha = async (testemunhaId) => {
    if (!window.confirm('Remover esta testemunha?')) return;
    try {
      const response = await axios.delete(`${API}/api/processos-juridicos/${id}/testemunhas/${testemunhaId}`, authHeaders());
      setProcesso(response.data);
      toast.success('Testemunha removida');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover testemunha');
    }
  };

  // --- Audiências ---

  const submitAudiencia = async () => {
    if (!audienciaForm.tipo.trim() || !audienciaForm.data) { toast.error('Informe o tipo e a data da audiência'); return; }
    if (audienciaForm.lembrete_ativo && (!audienciaForm.lembrete_email.trim() || !audienciaForm.lembrete_data)) {
      toast.error('Informe o e-mail e a data do lembrete');
      return;
    }
    try {
      setSaving(true);
      const dataHora = new Date(`${audienciaForm.data}T${audienciaForm.hora || '00:00'}`).toISOString();
      const payload = { ...audienciaForm, data: dataHora };
      delete payload.hora;
      if (audienciaForm.lembrete_ativo) {
        payload.lembrete_email = audienciaForm.lembrete_email.trim();
        payload.lembrete_data = new Date(`${audienciaForm.lembrete_data}T${audienciaForm.lembrete_hora || '00:00'}`).toISOString();
        payload.lembrete_repetir = audienciaForm.lembrete_repetir;
      } else {
        payload.lembrete_email = null;
        payload.lembrete_data = null;
        payload.lembrete_repetir = 'Não repetir';
      }
      delete payload.lembrete_ativo;
      delete payload.lembrete_hora;
      let response;
      if (editingAudienciaId) {
        response = await axios.put(`${API}/api/processos-juridicos/${id}/audiencias/${editingAudienciaId}`, payload, authHeaders());
        toast.success('Audiência atualizada');
      } else {
        response = await axios.post(`${API}/api/processos-juridicos/${id}/audiencias`, payload, authHeaders());
        toast.success('Audiência adicionada');
      }
      setProcesso(response.data);
      setShowAudienciaModal(false);
      setAudienciaForm(AUDIENCIA_FORM_DEFAULT);
      setEditingAudienciaId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar audiência');
    } finally {
      setSaving(false);
    }
  };

  const openEditAudiencia = (audiencia) => {
    const { date, time } = isoToDateTimeParts(audiencia.data);
    const lembrete = isoToDateTimeParts(audiencia.lembrete_data);
    setEditingAudienciaId(audiencia.id);
    setAudienciaForm({
      tipo: audiencia.tipo || '',
      data: date,
      hora: time,
      modalidade: audiencia.modalidade || 'Presencial',
      local_ou_plataforma: audiencia.local_ou_plataforma || '',
      link: audiencia.link || '',
      participantes: audiencia.participantes || '',
      notas: audiencia.notas || '',
      lembrete_ativo: !!audiencia.lembrete_email,
      lembrete_email: audiencia.lembrete_email || '',
      lembrete_data: lembrete.date,
      lembrete_hora: lembrete.time,
      lembrete_repetir: audiencia.lembrete_repetir || 'Não repetir',
    });
    setShowAudienciaModal(true);
  };

  const updateAudienciaStatus = async (audienciaId, status) => {
    try {
      const response = await axios.put(`${API}/api/processos-juridicos/${id}/audiencias/${audienciaId}`, { status }, authHeaders());
      setProcesso(response.data);
      toast.success('Audiência atualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar audiência');
    }
  };

  const removeAudiencia = async (audienciaId) => {
    if (!window.confirm('Remover esta audiência?')) return;
    try {
      const response = await axios.delete(`${API}/api/processos-juridicos/${id}/audiencias/${audienciaId}`, authHeaders());
      setProcesso(response.data);
      toast.success('Audiência removida');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover audiência');
    }
  };

  // --- Prazos ---

  const submitPrazo = async () => {
    if (!prazoForm.titulo.trim() || !prazoForm.data_limite) { toast.error('Informe o título e a data limite do prazo'); return; }
    try {
      setSaving(true);
      const dataHora = new Date(`${prazoForm.data_limite}T${prazoForm.hora_limite || '23:59'}`).toISOString();
      const payload = { ...prazoForm, data_limite: dataHora };
      delete payload.hora_limite;
      const response = await axios.post(`${API}/api/processos-juridicos/${id}/prazos`, payload, authHeaders());
      setProcesso(response.data);
      toast.success('Prazo adicionado');
      setShowPrazoModal(false);
      setPrazoForm({ titulo: '', tipo: '', data_limite: '', hora_limite: '', responsavel: '', prioridade: 'Baixa', providencia: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar prazo');
    } finally {
      setSaving(false);
    }
  };

  const updatePrazoStatus = async (prazoId, status) => {
    try {
      const response = await axios.put(`${API}/api/processos-juridicos/${id}/prazos/${prazoId}`, { status }, authHeaders());
      setProcesso(response.data);
      toast.success('Prazo atualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar prazo');
    }
  };

  const removePrazo = async (prazoId) => {
    if (!window.confirm('Remover este prazo?')) return;
    try {
      const response = await axios.delete(`${API}/api/processos-juridicos/${id}/prazos/${prazoId}`, authHeaders());
      setProcesso(response.data);
      toast.success('Prazo removido');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover prazo');
    }
  };

  // --- Movimentações ---

  const submitMovimentacao = async () => {
    if (!movimentacaoForm.titulo.trim()) { toast.error('Informe o título da movimentação'); return; }
    try {
      setSaving(true);
      const payload = {
        tipo: movimentacaoForm.tipo,
        titulo: movimentacaoForm.titulo,
        descricao: movimentacaoForm.descricao,
        data: movimentacaoForm.data ? new Date(movimentacaoForm.data).toISOString() : null,
      };
      const response = await axios.post(`${API}/api/processos-juridicos/${id}/movimentacoes`, payload, authHeaders());
      setProcesso(response.data);
      toast.success('Movimentação adicionada');
      setShowMovimentacaoModal(false);
      const geraPrazo = movimentacaoForm.geraPrazo === 'Sim';
      setMovimentacaoForm({ tipo: 'Tribunal', titulo: '', descricao: '', data: '', geraPrazo: 'Não', notificar: 'Sim' });
      if (geraPrazo) {
        setPrazoForm((p) => ({ ...p, titulo: payload.titulo, data_limite: '', hora_limite: '' }));
        setShowPrazoModal(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar movimentação');
    } finally {
      setSaving(false);
    }
  };

  // --- Documentos ---

  const submitDocumento = async () => {
    if (documentoFiles.length === 0 && !documentoForm.nome.trim()) {
      toast.error('Informe o nome do documento ou anexe um arquivo');
      return;
    }
    try {
      setSaving(true);
      let response;
      if (documentoFiles.length > 0) {
        for (const item of documentoFiles) {
          const form = new FormData();
          form.append('file', item.file);
          form.append('nome', item.nome.trim() || item.file.name);
          form.append('categoria', documentoForm.categoria || 'Outro');
          form.append('notas', documentoForm.notas || '');
          // eslint-disable-next-line no-await-in-loop
          response = await axios.post(`${API}/api/processos-juridicos/${id}/documentos/upload`, form, {
            headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
          });
        }
        toast.success(`${documentoFiles.length} documento(s) adicionado(s)`);
      } else {
        response = await axios.post(`${API}/api/processos-juridicos/${id}/documentos`, documentoForm, authHeaders());
        toast.success('Documento adicionado');
      }
      setProcesso(response.data);
      setShowDocumentoModal(false);
      setDocumentoForm({ nome: '', categoria: '', tamanho: '', notas: '' });
      setDocumentoFiles([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar documento');
    } finally {
      setSaving(false);
    }
  };

  const handleFilesSelected = (files) => {
    if (!files || files.length === 0) return;
    const items = Array.from(files).map((file) => ({ file, nome: file.name }));
    setDocumentoFiles((prev) => [...prev, ...items]);
  };

  const removeDocumento = async (documentoId) => {
    if (!window.confirm('Remover este documento?')) return;
    try {
      const response = await axios.delete(`${API}/api/processos-juridicos/${id}/documentos/${documentoId}`, authHeaders());
      setProcesso(response.data);
      toast.success('Documento removido');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover documento');
    }
  };

  const viewDocumento = async (doc) => {
    try {
      const response = await axios.get(`${API}/api/processos-juridicos/${id}/documentos/${doc.id}/view`, {
        ...authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.data.type }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Não foi possível visualizar o arquivo. Este documento pode não ter um arquivo anexado.');
    }
  };

  const downloadDocumento = async (doc) => {
    try {
      const response = await axios.get(`${API}/api/processos-juridicos/${id}/documentos/${doc.id}/download`, {
        ...authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.nome;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Não foi possível baixar o arquivo. Este documento pode não ter um arquivo anexado.');
    }
  };

  // --- Estratégia e Financeiro (atualizam o processo direto) ---

  const openEstrategiaModal = () => {
    setEstrategiaForm({
      tese_principal: processo.tese_principal || '',
      pontos_fortes: processo.pontos_fortes || '',
      pontos_fracos: processo.pontos_fracos || '',
      provas_disponiveis: processo.provas_disponiveis || '',
      proxima_providencia: processo.proxima_providencia || '',
      observacao_confidencial: processo.observacao_confidencial || '',
    });
    setShowEstrategiaModal(true);
  };

  const submitEstrategia = async () => {
    try {
      setSaving(true);
      const response = await axios.put(`${API}/api/processos-juridicos/${id}`, estrategiaForm, authHeaders());
      setProcesso(response.data);
      toast.success('Análise jurídica atualizada');
      setShowEstrategiaModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar análise');
    } finally {
      setSaving(false);
    }
  };

  const openFinanceiroModal = () => {
    setFinanceiroForm({
      valor_causa: processo.valor_causa ?? '',
      exposicao_estimada: processo.exposicao_estimada ?? '',
      valor_provisionado: processo.valor_provisionado ?? '',
      custas_processuais: processo.custas_processuais ?? '',
      honorarios_previstos: processo.honorarios_previstos ?? '',
      probabilidade_perda: processo.probabilidade_perda || '',
    });
    setShowFinanceiroModal(true);
  };

  const submitFinanceiro = async () => {
    try {
      setSaving(true);
      const payload = {
        ...financeiroForm,
        valor_causa: financeiroForm.valor_causa === '' ? null : parseFloat(financeiroForm.valor_causa),
        exposicao_estimada: financeiroForm.exposicao_estimada === '' ? null : parseFloat(financeiroForm.exposicao_estimada),
        valor_provisionado: financeiroForm.valor_provisionado === '' ? null : parseFloat(financeiroForm.valor_provisionado),
        custas_processuais: financeiroForm.custas_processuais === '' ? null : parseFloat(financeiroForm.custas_processuais),
        honorarios_previstos: financeiroForm.honorarios_previstos === '' ? null : parseFloat(financeiroForm.honorarios_previstos),
      };
      const response = await axios.put(`${API}/api/processos-juridicos/${id}`, payload, authHeaders());
      setProcesso(response.data);
      toast.success('Valores financeiros atualizados');
      setShowFinanceiroModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar valores');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Carregando processo...</div>;
  }

  if (!processo) {
    return (
      <div className="p-6 text-center text-gray-500">
        Processo não encontrado.
        <div className="mt-3">
          <Button variant="outline" onClick={() => navigate('/juridico/processos')}>Voltar para a lista</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate('/juridico/processos')} className="-ml-2">
        <ArrowLeft className="h-4 w-4 mr-2" /> Voltar para a lista
      </Button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{processo.numero_processo || processo.titulo}</h1>
            <Badge className={STATUS_COLOR[processo.status] || 'bg-gray-100 text-gray-800'}>{processo.status}</Badge>
            <Badge className={RISCO_COLOR[processo.risco] || 'bg-gray-100 text-gray-800'}>Risco: {processo.risco}</Badge>
          </div>
          <p className="text-gray-600 mt-1">{processo.titulo}</p>
        </div>
      </div>

      <Card>
        <CardContent className="py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1 text-sm">
            <div><span className="text-gray-500">Código interno: </span><span className="font-medium">{processo.codigo_interno || '-'}</span></div>
            <div><span className="text-gray-500">Tipo: </span><span className="font-medium">{processo.tipo_processo}</span></div>
            <div><span className="text-gray-500">Responsável: </span><span className="font-medium">{processo.responsible_name || '-'}</span></div>
            <div><span className="text-gray-500">Advogado: </span><span className="font-medium">{processo.lawyer || '-'}</span></div>
          </div>
          <div className="flex flex-wrap gap-x-6 mt-2 text-sm">
            {processo.board_id && (
              <div><span className="text-gray-500">Quadro: </span><span className="font-medium">{processo.board_name}</span></div>
            )}
            {processo.contract_id && (
              <div>
                <span className="text-gray-500">Contrato vinculado: </span>
                <span className="font-medium">{processo.contract_title}{processo.contract_number ? ` (${processo.contract_number})` : ''}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto">
          <TabsList className="w-max">
            <TabsTrigger value="resumo" className="flex items-center gap-1.5"><Scale className="h-4 w-4" />Resumo</TabsTrigger>
            <TabsTrigger value="partes" className="flex items-center gap-1.5"><Users className="h-4 w-4" />Partes e Representantes{processo.partes?.length > 0 ? ` (${processo.partes.length})` : ''}</TabsTrigger>
            <TabsTrigger value="audiencias" className="flex items-center gap-1.5"><Gavel className="h-4 w-4" />Audiências{processo.audiencias?.length > 0 ? ` (${processo.audiencias.length})` : ''}</TabsTrigger>
            <TabsTrigger value="testemunhas" className="flex items-center gap-1.5"><Users className="h-4 w-4" />Testemunhas{processo.testemunhas?.length > 0 ? ` (${processo.testemunhas.length})` : ''}</TabsTrigger>
            <TabsTrigger value="prazos" className="flex items-center gap-1.5"><Clock className="h-4 w-4" />Prazos e Providências{processo.prazos?.length > 0 ? ` (${processo.prazos.length})` : ''}</TabsTrigger>
            <TabsTrigger value="movimentacoes" className="flex items-center gap-1.5"><ListChecks className="h-4 w-4" />Movimentações{processo.movimentacoes?.length > 0 ? ` (${processo.movimentacoes.length})` : ''}</TabsTrigger>
            <TabsTrigger value="documentos" className="flex items-center gap-1.5"><FileStack className="h-4 w-4" />Documentos{processo.documentos?.length > 0 ? ` (${processo.documentos.length})` : ''}</TabsTrigger>
            <TabsTrigger value="estrategia" className="flex items-center gap-1.5"><Target className="h-4 w-4" />Estratégia e Análise</TabsTrigger>
            <TabsTrigger value="financeiro" className="flex items-center gap-1.5"><DollarSign className="h-4 w-4" />Financeiro</TabsTrigger>
            <TabsTrigger value="historico" className="flex items-center gap-1.5"><History className="h-4 w-4" />Histórico{historico.length > 0 ? ` (${historico.length})` : ''}</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="resumo" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Informações Gerais</CardTitle></CardHeader>
              <CardContent>
                <InfoRow label="Tipo do processo" value={processo.tipo_processo} />
                <InfoRow label="Tipo da ação" value={processo.tipo_acao} />
                <InfoRow label="Classe da ação" value={processo.classe_acao} />
                <InfoRow label="Assunto principal" value={processo.assunto_principal} />
                <InfoRow label="Tribunal" value={processo.tribunal} />
                <InfoRow label="Comarca" value={processo.comarca} />
                <InfoRow label="Vara / Órgão" value={processo.vara} />
                <InfoRow label="Juiz(a)" value={processo.juiz} />
                <InfoRow label="Competência" value={processo.competencia} />
                <InfoRow label="Sistema externo" value={processo.sistema_externo} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Situação</CardTitle></CardHeader>
              <CardContent>
                <InfoRow label="Situação atual" value={processo.situacao_atual} />
                <InfoRow label="Etapa interna" value={processo.etapa_interna} />
                <InfoRow label="Grau de sigilo" value={processo.grau_sigilo} />
                <InfoRow label="Probabilidade de perda" value={processo.probabilidade_perda} />
                <InfoRow label="Data de autuação" value={processo.data_autuacao ? formatDate(processo.data_autuacao) : null} />
                <InfoRow label="Atualizado em" value={formatDateTime(processo.updated_at)} />
                <InfoRow label="Criado por" value={processo.created_by_name ? `${processo.created_by_name} em ${formatDate(processo.created_at)}` : null} />
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Resumo do Caso</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-gray-700 whitespace-pre-wrap">{processo.resumo_caso || 'Sem resumo cadastrado.'}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Tese Principal</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-gray-700 whitespace-pre-wrap">{processo.tese_principal || 'Sem tese cadastrada.'}</p></CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* --- Partes e Representantes --- */}
        <TabsContent value="partes" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Partes e Representantes</h2>
            <Button size="sm" onClick={openAddParte}><Plus className="h-4 w-4 mr-2" />Adicionar parte</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Participação</TableHead>
                    <TableHead>Nome / Razão Social</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Representante</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!processo.partes || processo.partes.length === 0) ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Nenhuma parte cadastrada ainda.</TableCell></TableRow>
                  ) : processo.partes.map((parte) => (
                    <TableRow key={parte.id}>
                      <TableCell><Badge variant="outline">{parte.papel}</Badge></TableCell>
                      <TableCell className="font-medium">{parte.nome}</TableCell>
                      <TableCell>{parte.documento || '-'}</TableCell>
                      <TableCell>{parte.tipo_pessoa || '-'}</TableCell>
                      <TableCell>{parte.representante || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditParte(parte)}>Editar</Button>
                          <Button variant="ghost" size="sm" onClick={() => removeParte(parte.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Audiências --- */}
        <TabsContent value="audiencias" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Audiências</h2>
            <Button
              size="sm"
              onClick={() => {
                setEditingAudienciaId(null);
                setAudienciaForm(AUDIENCIA_FORM_DEFAULT);
                setShowAudienciaModal(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />Nova audiência
            </Button>
          </div>
          {(!processo.audiencias || processo.audiencias.length === 0) ? (
            <Card><CardContent className="py-10 text-center text-sm text-gray-500">Nenhuma audiência cadastrada ainda.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {processo.audiencias.map((audiencia) => (
                <Card key={audiencia.id}>
                  <CardContent className="py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">{audiencia.modalidade || '-'}</Badge>
                      <Badge className={AUDIENCIA_STATUS_COLOR[audiencia.status] || 'bg-gray-100 text-gray-800'}>{audiencia.status}</Badge>
                    </div>
                    <h3 className="font-semibold">{audiencia.tipo}</h3>
                    <p className="text-sm"><span className="font-semibold">Data:</span> {formatDateTime(audiencia.data)}</p>
                    {audiencia.local_ou_plataforma && <p className="text-sm"><span className="font-semibold">Plataforma:</span> {audiencia.local_ou_plataforma}</p>}
                    {audiencia.participantes && <p className="text-sm"><span className="font-semibold">Participantes:</span> {audiencia.participantes}</p>}
                    {audiencia.resultado && <p className="text-sm"><span className="font-semibold">Resultado:</span> {audiencia.resultado}</p>}
                    <div className="flex items-center gap-2 pt-1">
                      <Select value={audiencia.status} onValueChange={(v) => updateAudienciaStatus(audiencia.id, v)}>
                        <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {AUDIENCIA_STATUS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => openEditAudiencia(audiencia)}><Edit className="h-4 w-4 text-gray-500" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => removeAudiencia(audiencia.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* --- Testemunhas --- */}
        <TabsContent value="testemunhas" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Testemunhas</h2>
            <Button size="sm" onClick={openAddTestemunha}><Plus className="h-4 w-4 mr-2" />Adicionar testemunha</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead>Assunto</TableHead>
                    <TableHead>Audiência vinculada</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!processo.testemunhas || processo.testemunhas.length === 0) ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Nenhuma testemunha cadastrada ainda.</TableCell></TableRow>
                  ) : processo.testemunhas.map((testemunha) => {
                    const audienciaVinculada = processo.audiencias?.find((a) => a.id === testemunha.audiencia_id);
                    return (
                      <TableRow key={testemunha.id}>
                        <TableCell className="font-medium">{testemunha.nome}</TableCell>
                        <TableCell>{testemunha.contato || '-'}</TableCell>
                        <TableCell>{testemunha.assunto || '-'}</TableCell>
                        <TableCell>{audienciaVinculada ? `${audienciaVinculada.tipo} (${formatDate(audienciaVinculada.data)})` : '-'}</TableCell>
                        <TableCell>
                          <Select value={testemunha.status} onValueChange={(v) => updateTestemunhaStatus(testemunha.id, v)}>
                            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {TESTEMUNHA_STATUS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => removeTestemunha(testemunha.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Prazos e Providências --- */}
        <TabsContent value="prazos" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Prazos e Providências</h2>
            <Button size="sm" onClick={() => setShowPrazoModal(true)}><Plus className="h-4 w-4 mr-2" />Novo prazo</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prazo</TableHead>
                    <TableHead>Data limite</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Prioridade</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!processo.prazos || processo.prazos.length === 0) ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Nenhum prazo cadastrado ainda.</TableCell></TableRow>
                  ) : processo.prazos.map((prazo) => (
                    <TableRow key={prazo.id}>
                      <TableCell>
                        <div className="font-medium">{prazo.titulo}</div>
                        {prazo.providencia && <div className="text-xs text-gray-500">{prazo.providencia}</div>}
                      </TableCell>
                      <TableCell>{formatDate(prazo.data_limite)}</TableCell>
                      <TableCell>{prazo.responsavel || '-'}</TableCell>
                      <TableCell><Badge className={PRIORIDADE_COLOR[prazo.prioridade] || 'bg-gray-100 text-gray-800'}>{prazo.prioridade}</Badge></TableCell>
                      <TableCell>
                        <Select value={prazo.status} onValueChange={(v) => updatePrazoStatus(prazo.id, v)}>
                          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PRAZO_STATUS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => removePrazo(prazo.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Movimentações --- */}
        <TabsContent value="movimentacoes" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Movimentações Processuais</h2>
            <Button size="sm" onClick={() => setShowMovimentacaoModal(true)}><Plus className="h-4 w-4 mr-2" />Nova movimentação</Button>
          </div>
          {(!processo.movimentacoes || processo.movimentacoes.length === 0) ? (
            <Card><CardContent className="py-10 text-center text-sm text-gray-500">Nenhuma movimentação registrada ainda.</CardContent></Card>
          ) : (
            <Card>
              <CardContent className="py-4">
                <div className="pl-4">
                  {[...processo.movimentacoes].sort((a, b) => new Date(b.data) - new Date(a.data)).map((m, idx, arr) => (
                    <div key={m.id} className={`relative pl-4 pb-5 border-l-2 border-gray-200 ${idx === arr.length - 1 ? 'border-transparent' : ''}`}>
                      <span className={`absolute -left-[7px] top-0 h-3 w-3 rounded-full border-2 border-white ${MOVIMENTACAO_DOT_COLOR[m.tipo] || 'bg-gray-400'}`} />
                      <p className="text-xs text-gray-500">{formatDateTime(m.data)}</p>
                      <h3 className="font-semibold text-sm mt-0.5">{m.titulo}</h3>
                      {m.descricao && <p className="text-sm text-gray-600 mt-0.5">{m.descricao}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* --- Documentos --- */}
        <TabsContent value="documentos" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Documentos</h2>
            <Button size="sm" onClick={() => setShowDocumentoModal(true)}><Plus className="h-4 w-4 mr-2" />Enviar documento</Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Documento</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Tamanho</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!processo.documentos || processo.documentos.length === 0) ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Nenhum documento cadastrado ainda.</TableCell></TableRow>
                  ) : processo.documentos.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <span className="font-medium">{doc.nome}</span>
                        </div>
                      </TableCell>
                      <TableCell>{doc.categoria || 'Outro'}</TableCell>
                      <TableCell>{formatDate(doc.created_at)}</TableCell>
                      <TableCell>{doc.created_by_name || '-'}</TableCell>
                      <TableCell>{doc.tamanho || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" disabled={!doc.gridfs_file_id} title="Visualizar" onClick={() => viewDocumento(doc)}><Eye className="h-4 w-4 text-gray-500" /></Button>
                        <Button variant="ghost" size="sm" disabled={!doc.gridfs_file_id} title="Baixar" onClick={() => downloadDocumento(doc)}><Download className="h-4 w-4 text-gray-500" /></Button>
                        <Button variant="ghost" size="sm" title="Excluir" onClick={() => removeDocumento(doc.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Estratégia e Análise Jurídica --- */}
        <TabsContent value="estrategia" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Estratégia e Análise Jurídica</h2>
            <Button size="sm" onClick={openEstrategiaModal}><Edit className="h-4 w-4 mr-2" />Editar análise</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Tese principal</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-gray-700 whitespace-pre-wrap">{processo.tese_principal || '-'}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Pontos fortes</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-gray-700 whitespace-pre-wrap">{processo.pontos_fortes || '-'}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Pontos fracos</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-gray-700 whitespace-pre-wrap">{processo.pontos_fracos || '-'}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Provas disponíveis</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-gray-700 whitespace-pre-wrap">{processo.provas_disponiveis || '-'}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Próxima providência</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-gray-700 whitespace-pre-wrap">{processo.proxima_providencia || '-'}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Observação confidencial</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-gray-700 whitespace-pre-wrap">{processo.observacao_confidencial || 'Área restrita aos usuários autorizados do quadro.'}</p></CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* --- Financeiro --- */}
        <TabsContent value="financeiro" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Financeiro Jurídico</h2>
            <Button size="sm" onClick={openFinanceiroModal}><Edit className="h-4 w-4 mr-2" />Editar valores</Button>
          </div>
          <Card>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4">
              <div className="text-center border rounded-md p-3">
                <div className="text-xs text-gray-500">Valor da Causa</div>
                <div className="text-lg font-semibold mt-1">{formatCurrency(processo.valor_causa)}</div>
                <div className="text-xs text-gray-400">Valor atualizado</div>
              </div>
              <div className="text-center border rounded-md p-3">
                <div className="text-xs text-gray-500">Exposição Estimada</div>
                <div className="text-lg font-semibold mt-1">{formatCurrency(processo.exposicao_estimada)}</div>
                <div className="text-xs text-gray-400">Estimativa jurídica</div>
              </div>
              <div className="text-center border rounded-md p-3">
                <div className="text-xs text-gray-500">Valor Provisionado</div>
                <div className="text-lg font-semibold mt-1">{formatCurrency(processo.valor_provisionado)}</div>
                <div className="text-xs text-gray-400">
                  {processo.valor_causa && processo.valor_provisionado ? `${Math.round((processo.valor_provisionado / processo.valor_causa) * 100)}% da causa` : '-'}
                </div>
              </div>
              <div className="text-center border rounded-md p-3">
                <div className="text-xs text-gray-500">Custas</div>
                <div className="text-lg font-semibold mt-1">{formatCurrency(processo.custas_processuais)}</div>
                <div className="text-xs text-gray-400">Pagas</div>
              </div>
              <div className="text-center border rounded-md p-3">
                <div className="text-xs text-gray-500">Honorários</div>
                <div className="text-lg font-semibold mt-1">{formatCurrency(processo.honorarios_previstos)}</div>
                <div className="text-xs text-gray-400">Previstos</div>
              </div>
              <div className="text-center border rounded-md p-3 border-red-200">
                <div className="text-xs text-gray-500">Probabilidade de perda</div>
                <div className="text-lg font-semibold mt-1 text-red-600">{processo.probabilidade_perda || '-'}</div>
                <div className="text-xs text-gray-400">Classificação jurídica</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historico" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Histórico de Auditoria</h2>
            <Button variant="outline" size="sm" onClick={exportHistorico}>
              <Download className="h-4 w-4 mr-2" />Exportar histórico
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {historico.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">Nenhum evento registrado ainda.</div>
              ) : (
                <div className="divide-y">
                  {historico.map((h) => (
                    <div key={h.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm capitalize">{h.action}</span>
                        <span className="text-xs text-gray-500">{formatDateTime(h.performed_at)}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{h.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* --- Modal: Adicionar/Editar parte --- */}
      <Dialog open={showParteModal} onOpenChange={setShowParteModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingParteId ? 'Editar parte' : 'Adicionar parte'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de participação</Label>
              <Select value={parteForm.papel} onValueChange={(v) => setParteForm((p) => ({ ...p, papel: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAPEL_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo de pessoa</Label>
              <Select value={parteForm.tipo_pessoa} onValueChange={(v) => setParteForm((p) => ({ ...p, tipo_pessoa: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPO_PESSOA_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Nome ou razão social *</Label>
              <Input value={parteForm.nome} onChange={(e) => setParteForm((p) => ({ ...p, nome: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>CPF ou CNPJ</Label>
              <Input value={parteForm.documento} onChange={(e) => setParteForm((p) => ({ ...p, documento: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Representante</Label>
              <Input value={parteForm.representante} onChange={(e) => setParteForm((p) => ({ ...p, representante: e.target.value }))} />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={parteForm.notas} onChange={(e) => setParteForm((p) => ({ ...p, notas: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowParteModal(false)}>Cancelar</Button>
            <Button disabled={saving} onClick={submitParte}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Modal: Nova testemunha --- */}
      <Dialog open={showTestemunhaModal} onOpenChange={setShowTestemunhaModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adicionar testemunha</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Nome *</Label>
              <Input value={testemunhaForm.nome} onChange={(e) => setTestemunhaForm((p) => ({ ...p, nome: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Contato</Label>
              <Input value={testemunhaForm.contato} onChange={(e) => setTestemunhaForm((p) => ({ ...p, contato: e.target.value }))} placeholder="Telefone ou e-mail" />
            </div>
            <div className="space-y-2">
              <Label>Audiência vinculada</Label>
              <Select value={testemunhaForm.audiencia_id || 'none'} onValueChange={(v) => setTestemunhaForm((p) => ({ ...p, audiencia_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {(processo.audiencias || []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.tipo} ({formatDate(a.data)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Assunto (sobre o que vai testemunhar)</Label>
              <Textarea rows={2} value={testemunhaForm.assunto} onChange={(e) => setTestemunhaForm((p) => ({ ...p, assunto: e.target.value }))} />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={testemunhaForm.notas} onChange={(e) => setTestemunhaForm((p) => ({ ...p, notas: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestemunhaModal(false)}>Cancelar</Button>
            <Button disabled={saving} onClick={submitTestemunha}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Modal: Nova/Editar audiência --- */}
      <Dialog
        open={showAudienciaModal}
        onOpenChange={(open) => {
          setShowAudienciaModal(open);
          if (!open) {
            setAudienciaForm(AUDIENCIA_FORM_DEFAULT);
            setEditingAudienciaId(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingAudienciaId ? 'Editar audiência' : 'Nova audiência'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Tipo da audiência *</Label>
              <Input value={audienciaForm.tipo} onChange={(e) => setAudienciaForm((p) => ({ ...p, tipo: e.target.value }))} placeholder="Ex: Instrução e Julgamento" />
            </div>
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input type="date" value={audienciaForm.data} onChange={(e) => setAudienciaForm((p) => ({ ...p, data: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Horário</Label>
              <Input type="time" value={audienciaForm.hora} onChange={(e) => setAudienciaForm((p) => ({ ...p, hora: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Modalidade</Label>
              <Select value={audienciaForm.modalidade} onValueChange={(v) => setAudienciaForm((p) => ({ ...p, modalidade: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MODALIDADE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Plataforma ou local</Label>
              <Input value={audienciaForm.local_ou_plataforma} onChange={(e) => setAudienciaForm((p) => ({ ...p, local_ou_plataforma: e.target.value }))} />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Link da reunião</Label>
              <Input value={audienciaForm.link} onChange={(e) => setAudienciaForm((p) => ({ ...p, link: e.target.value }))} />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Participantes</Label>
              <Textarea rows={2} value={audienciaForm.participantes} onChange={(e) => setAudienciaForm((p) => ({ ...p, participantes: e.target.value }))} />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={audienciaForm.notas} onChange={(e) => setAudienciaForm((p) => ({ ...p, notas: e.target.value }))} />
            </div>

            <div className="md:col-span-2 border-t pt-4 space-y-2">
              <Label>Enviar lembrete por e-mail?</Label>
              <Select
                value={audienciaForm.lembrete_ativo ? 'Sim' : 'Não'}
                onValueChange={(v) => setAudienciaForm((p) => ({ ...p, lembrete_ativo: v === 'Sim' }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Não">Não</SelectItem>
                  <SelectItem value="Sim">Sim</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {audienciaForm.lembrete_ativo && (
              <>
                <div className="md:col-span-2 space-y-2">
                  <Label>E-mail para o lembrete *</Label>
                  <Input
                    type="email"
                    value={audienciaForm.lembrete_email}
                    onChange={(e) => setAudienciaForm((p) => ({ ...p, lembrete_email: e.target.value }))}
                    placeholder="nome@exemplo.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data do lembrete *</Label>
                  <Input
                    type="date"
                    value={audienciaForm.lembrete_data}
                    onChange={(e) => setAudienciaForm((p) => ({ ...p, lembrete_data: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horário do lembrete</Label>
                  <Input
                    type="time"
                    value={audienciaForm.lembrete_hora}
                    onChange={(e) => setAudienciaForm((p) => ({ ...p, lembrete_hora: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>Repetir lembrete</Label>
                  <Select
                    value={audienciaForm.lembrete_repetir}
                    onValueChange={(v) => setAudienciaForm((p) => ({ ...p, lembrete_repetir: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEMBRETE_REPETIR_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400">
                    {audienciaForm.lembrete_repetir === 'Não repetir'
                      ? 'O lembrete é enviado uma única vez, na data e horário definidos.'
                      : `O lembrete se repete ${audienciaForm.lembrete_repetir.toLowerCase()} a partir da data definida, até a data da audiência.`}
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAudienciaModal(false);
                setAudienciaForm(AUDIENCIA_FORM_DEFAULT);
                setEditingAudienciaId(null);
              }}
            >
              Cancelar
            </Button>
            <Button disabled={saving} onClick={submitAudiencia}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Modal: Novo prazo --- */}
      <Dialog open={showPrazoModal} onOpenChange={setShowPrazoModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo prazo</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Nome do prazo *</Label>
              <Input value={prazoForm.titulo} onChange={(e) => setPrazoForm((p) => ({ ...p, titulo: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={prazoForm.tipo || 'none'} onValueChange={(v) => setPrazoForm((p) => ({ ...p, tipo: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não definido</SelectItem>
                  {PRAZO_TIPO_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Data limite *</Label>
              <Input type="date" value={prazoForm.data_limite} onChange={(e) => setPrazoForm((p) => ({ ...p, data_limite: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Horário limite</Label>
              <Input type="time" value={prazoForm.hora_limite} onChange={(e) => setPrazoForm((p) => ({ ...p, hora_limite: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Responsável</Label>
              <Input value={prazoForm.responsavel} onChange={(e) => setPrazoForm((p) => ({ ...p, responsavel: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select value={prazoForm.prioridade} onValueChange={(v) => setPrazoForm((p) => ({ ...p, prioridade: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORIDADE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Providência</Label>
              <Textarea rows={2} value={prazoForm.providencia} onChange={(e) => setPrazoForm((p) => ({ ...p, providencia: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrazoModal(false)}>Cancelar</Button>
            <Button disabled={saving} onClick={submitPrazo}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Modal: Nova movimentação --- */}
      <Dialog open={showMovimentacaoModal} onOpenChange={setShowMovimentacaoModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova movimentação</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data</Label>
              <Input type="datetime-local" value={movimentacaoForm.data} onChange={(e) => setMovimentacaoForm((p) => ({ ...p, data: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={movimentacaoForm.tipo} onValueChange={(v) => setMovimentacaoForm((p) => ({ ...p, tipo: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MOVIMENTACAO_TIPO_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Título *</Label>
              <Input value={movimentacaoForm.titulo} onChange={(e) => setMovimentacaoForm((p) => ({ ...p, titulo: e.target.value }))} />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Descrição</Label>
              <Textarea rows={3} value={movimentacaoForm.descricao} onChange={(e) => setMovimentacaoForm((p) => ({ ...p, descricao: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Gera prazo?</Label>
              <Select value={movimentacaoForm.geraPrazo} onValueChange={(v) => setMovimentacaoForm((p) => ({ ...p, geraPrazo: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Não">Não</SelectItem><SelectItem value="Sim">Sim</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notificar responsáveis?</Label>
              <Select value={movimentacaoForm.notificar} onValueChange={(v) => setMovimentacaoForm((p) => ({ ...p, notificar: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Sim">Sim</SelectItem><SelectItem value="Não">Não</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMovimentacaoModal(false)}>Cancelar</Button>
            <Button disabled={saving} onClick={submitMovimentacao}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Modal: Novo documento --- */}
      <Dialog
        open={showDocumentoModal}
        onOpenChange={(open) => {
          setShowDocumentoModal(open);
          if (!open) {
            setDocumentoForm({ nome: '', categoria: '', tamanho: '', notas: '' });
            setDocumentoFiles([]);
          }
        }}
      >
        <DialogContent>
          <DialogHeader><DialogTitle>Novo documento</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label>Anexar arquivo</Label>
              <div
                className="min-h-[90px] border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-primary hover:text-primary cursor-pointer transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFilesSelected(e.dataTransfer.files); }}
              >
                <Upload className="h-5 w-5 mb-1" />
                <p className="font-medium text-sm">Arraste documentos aqui</p>
                <p className="text-xs">ou clique para selecionar arquivos</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => { handleFilesSelected(e.target.files); e.target.value = ''; }}
                />
              </div>
              {documentoFiles.length > 0 && (
                <div className="space-y-1">
                  {documentoFiles.map((item, i) => (
                    <div key={`${item.file.name}-${i}`} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-2 py-1">
                      <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                      <Input
                        className="h-7 text-sm"
                        value={item.nome}
                        onChange={(e) => {
                          const nome = e.target.value;
                          setDocumentoFiles((prev) => prev.map((it, idx) => (idx === i ? { ...it, nome } : it)));
                        }}
                      />
                      <span className="text-xs text-gray-400 shrink-0">({formatFileSize(item.file.size)})</span>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-500 shrink-0"
                        onClick={() => setDocumentoFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {documentoFiles.length === 0 && (
              <div className="space-y-2">
                <Label>Nome do documento *</Label>
                <Input
                  value={documentoForm.nome}
                  onChange={(e) => setDocumentoForm((p) => ({ ...p, nome: e.target.value }))}
                  placeholder="Ex: Petição Inicial.pdf"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={documentoForm.categoria || 'none'} onValueChange={(v) => setDocumentoForm((p) => ({ ...p, categoria: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não definida</SelectItem>
                  {DOCUMENTO_CATEGORIA_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={documentoForm.notas} onChange={(e) => setDocumentoForm((p) => ({ ...p, notas: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDocumentoModal(false);
                setDocumentoForm({ nome: '', categoria: '', tamanho: '', notas: '' });
                setDocumentoFiles([]);
              }}
            >
              Cancelar
            </Button>
            <Button disabled={saving} onClick={submitDocumento}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Modal: Editar análise jurídica --- */}
      <Dialog open={showEstrategiaModal} onOpenChange={setShowEstrategiaModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar análise jurídica</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label>Tese principal</Label>
              <Textarea rows={2} value={estrategiaForm.tese_principal} onChange={(e) => setEstrategiaForm((p) => ({ ...p, tese_principal: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Pontos fortes</Label>
              <Textarea rows={2} value={estrategiaForm.pontos_fortes} onChange={(e) => setEstrategiaForm((p) => ({ ...p, pontos_fortes: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Pontos fracos</Label>
              <Textarea rows={2} value={estrategiaForm.pontos_fracos} onChange={(e) => setEstrategiaForm((p) => ({ ...p, pontos_fracos: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Provas disponíveis</Label>
              <Textarea rows={2} value={estrategiaForm.provas_disponiveis} onChange={(e) => setEstrategiaForm((p) => ({ ...p, provas_disponiveis: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Próxima providência</Label>
              <Textarea rows={2} value={estrategiaForm.proxima_providencia} onChange={(e) => setEstrategiaForm((p) => ({ ...p, proxima_providencia: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Observação confidencial</Label>
              <Textarea rows={2} value={estrategiaForm.observacao_confidencial} onChange={(e) => setEstrategiaForm((p) => ({ ...p, observacao_confidencial: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEstrategiaModal(false)}>Cancelar</Button>
            <Button disabled={saving} onClick={submitEstrategia}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Modal: Editar valores financeiros --- */}
      <Dialog open={showFinanceiroModal} onOpenChange={setShowFinanceiroModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar valores financeiros</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Valor da causa</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">R$</span>
                <Input
                  className="pl-8"
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={formatCurrencyDisplay(financeiroForm.valor_causa)}
                  onChange={(e) => setFinanceiroForm((p) => ({ ...p, valor_causa: parseCurrencyInput(e.target.value) }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Exposição estimada</Label>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={formatCurrencyDisplay(financeiroForm.exposicao_estimada)}
                onChange={(e) => setFinanceiroForm((p) => ({ ...p, exposicao_estimada: parseCurrencyInput(e.target.value) }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Valor provisionado</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">R$</span>
                <Input
                  className="pl-8"
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={formatCurrencyDisplay(financeiroForm.valor_provisionado)}
                  onChange={(e) => setFinanceiroForm((p) => ({ ...p, valor_provisionado: parseCurrencyInput(e.target.value) }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Custas processuais</Label>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={formatCurrencyDisplay(financeiroForm.custas_processuais)}
                onChange={(e) => setFinanceiroForm((p) => ({ ...p, custas_processuais: parseCurrencyInput(e.target.value) }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Honorários previstos</Label>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={formatCurrencyDisplay(financeiroForm.honorarios_previstos)}
                onChange={(e) => setFinanceiroForm((p) => ({ ...p, honorarios_previstos: parseCurrencyInput(e.target.value) }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Probabilidade de perda</Label>
              <Select value={financeiroForm.probabilidade_perda || 'none'} onValueChange={(v) => setFinanceiroForm((p) => ({ ...p, probabilidade_perda: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Não definida" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não definida</SelectItem>
                  {PROBABILIDADE_PERDA_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinanceiroModal(false)}>Cancelar</Button>
            <Button disabled={saving} onClick={submitFinanceiro}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestaoProcessoDetailPage;
